import { memo } from 'react'
import type { Components } from 'react-markdown'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'
import { cn } from '@/utils/cn'
import { useTypewriter } from '@/hooks/useTypewriter'
import { WidgetRenderer } from '@/components/widgets'
import { FeedbackButtons } from './FeedbackButtons'
import { SuggestionChips } from './SuggestionChips'
import type { Message as MessageType, WidgetActionHandler } from '@/types'

// Hoisted outside component to avoid re-creating on every render.
// ReactMarkdown diffs components by reference — inline objects cause re-parses.
const sharedComponents = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-2 last:mb-0">{children}</p>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="[&>p]:inline">{children}</li>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="m-4 list-disc">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="m-4 list-decimal">{children}</ol>
  ),
}

const PROBABLY_PHONE_NUMBER = /[0-9\-()]{7,}/

// Does *User* markdown probably doesn't even make sense... would they care that we don't format
const userMarkdownComponents: Components = {
  ...sharedComponents,
  a: ({ children, href, ...props }) => {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="underline text-white hover:text-primary-100 hover:cursor-pointer"
        {...props}
      >
        {children}
        <span className="sr-only"> (opens in new tab)</span>
      </a>
    )
  },
}

const agentMarkdownComponents: Components = {
  ...sharedComponents,
  a: ({ children, href, ...props }) => {
    const isPhoneNumber =
      !href &&
      typeof children === 'string' &&
      PROBABLY_PHONE_NUMBER.test(children)

    if (isPhoneNumber) {
      href = `tel:${children.replace(/\s+/g, '')}`
    }

    // Opens the phone dialer from the top-level context. iOS Safari requires
    // an anchor click outside the iframe to handle the `tel:` scheme.
    if (href?.startsWith('tel:')) {
      return (
        <a
          href={href}
          target="_top"
          className="underline text-primary-600 hover:text-primary-700"
          {...props}
        >
          {children}
        </a>
      )
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="underline text-primary-600 hover:text-primary-700"
        {...props}
      >
        {children}
        <span className="sr-only"> (opens in new tab)</span>
      </a>
    )
  },
}

export interface MessageProps {
  message: MessageType
  onWidgetAction?: WidgetActionHandler
  onVote?: (messageId: string, wasHelpful: boolean | null) => void
  isVoting?: boolean
  onSuggestionSelect?: (text: string) => void
  suggestionsDisabled?: boolean
  /** Gates contextual suggested chips (follow-ups and error fallbacks). Off by default. */
  suggestedChipsEnabled?: boolean
  /** When true, reveal streamed text character-by-character (active stream only). */
  animate?: boolean
}

/**
 * One text segment's markdown, revealed character-by-character while it is
 * the actively-streaming tail. The typewriter hook keeps revealing until it
 * catches up even after `animate` flips off (segment stops being the tail
 * when a widget lands, or the stream finalizes) — so nothing snaps in.
 */
function AnimatedMarkdownText({
  text,
  animate,
  components,
}: {
  text: string
  animate: boolean
  components: Components
}) {
  const revealed = useTypewriter(text, animate)
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSanitize]}
      components={components}
    >
      {revealed.replace(/^[ \t]*•/gm, '-')}
    </ReactMarkdown>
  )
}

export const Message = memo(function Message({
  message,
  onWidgetAction,
  onVote,
  isVoting,
  onSuggestionSelect,
  suggestionsDisabled,
  suggestedChipsEnabled = false,
  animate = false,
}: MessageProps) {
  const isUser = message.sender === 'user'
  const isError = message.type === 'error'
  const isToolCall = message.type === 'tool_call'

  // Format timestamp for screen readers
  const timeString = new Date(message.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })

  // Tool messages are rendered differently (smaller, more subtle)
  if (isToolCall) {
    return (
      <div
        className="flex w-full justify-start animate-fade-in"
        role="listitem"
      >
        <div className="max-w-[85%] rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-600">
          {isToolCall && (
            <div className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
              <span>{message.display}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  const suggestions =
    suggestedChipsEnabled &&
    !isUser &&
    (message.type === 'text' || message.type === 'error')
      ? message.suggestions
      : undefined

  // A widget lays itself out, so its bubble takes the full allowed width rather
  // than shrink-wrapping its widest row. Without this the bubble resizes
  // whenever a widget's own content changes width — e.g. collapsing a day in
  // the callback slot picker.
  const hasWidgetSegment =
    message.type === 'text' &&
    message.segments.some((segment) => segment.type === 'widget')

  return (
    <>
      <div
        className={cn(
          'flex w-full animate-fade-in',
          isUser ? 'justify-end' : 'justify-start',
        )}
        role="listitem"
      >
        <article
          className={cn(
            'min-w-0 max-w-[93%] md:max-w-[85%] rounded-lg px-4 py-3',
            hasWidgetSegment && 'w-full',
            isUser
              ? 'bg-primary-600 text-white'
              : isError
                ? 'border border-error-200 bg-error-50 text-error-800'
                : 'bg-gray-100 text-gray-900',
          )}
          aria-label={`${isUser ? 'You' : 'Assistant'} at ${timeString}`}
        >
          {/* Text message: server-resolved segments interleave markdown prose
              and widgets. Widgets render as they arrive; while the stream is
              live (`animate`) the trailing text segment — the one deltas are
              appending to — reveals character-by-character. */}
          {message.type === 'text' && (
            <>
              {message.segments.map((segment, segmentIdx) =>
                segment.type === 'text' ? (
                  <AnimatedMarkdownText
                    key={`${message.id}-seg-${segmentIdx}`}
                    text={segment.content}
                    animate={
                      animate && segmentIdx === message.segments.length - 1
                    }
                    components={
                      isUser ? userMarkdownComponents : agentMarkdownComponents
                    }
                  />
                ) : (
                  <div
                    key={`${message.id}-seg-${segmentIdx}`}
                    className="my-3 min-w-24"
                  >
                    <WidgetRenderer
                      type={segment.widgetType}
                      data={segment.data}
                      onAction={onWidgetAction}
                    />
                  </div>
                ),
              )}

              {/* Feedback buttons for agent messages */}
              {message.type === 'text' &&
                !isUser &&
                message.serverId &&
                onVote && (
                  <div className="mt-2 flex items-center gap-2">
                    <FeedbackButtons
                      messageId={message.id}
                      wasHelpful={message.wasHelpful}
                      onVote={onVote}
                      disabled={isVoting}
                    />
                  </div>
                )}
            </>
          )}

          {/* Error message */}
          {message.type === 'error' && (
            <div className="flex items-start gap-2" role="alert">
              <span aria-hidden="true" className="text-error-500">
                {'\u26A0'} {/* Warning sign symbol */}
              </span>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSanitize]}
                components={agentMarkdownComponents}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}

          {/* Timestamp - visible on hover/focus */}
          <time
            dateTime={new Date(message.timestamp).toISOString()}
            className={cn(
              'mt-1 block text-xs opacity-0 transition-opacity',
              'group-hover:opacity-100 group-focus-within:opacity-100',
              isUser ? 'text-primary-200' : 'text-gray-500',
            )}
          >
            {timeString}
          </time>
        </article>
      </div>

      {/* Contextual follow-up chips, rendered beneath the assistant bubble */}
      {suggestions && suggestions.length > 0 && onSuggestionSelect && (
        <div className="flex w-full justify-start">
          <SuggestionChips
            chips={suggestions}
            onSelect={onSuggestionSelect}
            disabled={suggestionsDisabled}
            className="max-w-[93%] md:max-w-[85%]"
          />
        </div>
      )}
    </>
  )
})
